/**
 * @description Service layer for the flight modify booking workflow.
 *              Wraps the nine ops-facing endpoints documented in
 *              Modification-flow.docx v9 (SMB-2656 + SMB-2905 + SMB-2906 +
 *              SMB-2909). Customer-side endpoints (/select-sectors,
 *              /approve-quote, /decline-quote) are intentionally NOT wrapped
 *              here -- those are called from MyTrip, not the ops dashboard.
 *
 *              Each function generates a fresh x-trace-id per call (UUID v4)
 *              and forwards it through apiClient. The trace id is echoed back
 *              by the backend and is surfaced in the UI on error.
 *
 *              Path prefix assumed for the gateway: BE_ADMIN_BASE + /flight/modify
 *              (engine: /be/v1/flight/modify -- gateway team maps).
 *
 * @author: Azamuddin
 * @date: 05/21/2026
 */

//********IMPORTS STARTS*********/
import { BE_ADMIN_BASE } from "./environment";
// @ts-ignore -- apiClient is JS; named exports are valid at runtime
import { apiGet, apiPost, genTraceId } from "./apiClient";
//********IMPORTS ENDS*********/

//********CONSTANTS STARTS*********/
const MODIFY_BASE = `${BE_ADMIN_BASE}/flight/modify`;
//********CONSTANTS ENDS*********/

//********TYPES STARTS*********/
export type ModState =
    | "new_request"
    | "checking"
    | "quote_ready"
    | "sent_to_customer"
    | "customer_approved"
    | "customer_rejected"
    | "refund_pending"
    | "refund_complete";

export type BackendStatus =
    | "IN_PROGRESS"
    | "QUOTE_PENDING"
    | "QUOTE_READY"
    | "CUST_REVIEW"
    | "PAY_PENDING"
    | "PAY_SUCCESS"
    | "ISSUING"
    | "ISSUED"
    | "CONFIRMED"
    | "COMPLETE"
    | "CANCELLED"
    | "AUTO_CANCELLED"
    | "QUOTE_EXPIRED"
    | "ISSUANCE_FAILED"
    | "REFUND_PEND"
    | "REFUND_COMPLETE"
    | "FAILED";

export type FlowType = "AUTO" | "MANUAL";
export type QuoteSource = "AUTO" | "MANUAL";
export type PaxType = "ADT" | "CHD" | "INF";

export interface PaxRates {
    fareDiff: number;
    changeFee: number;
    tax: number;
    totalDiff: number;
}

export interface Sector {
    transactionId: string;
    sectorId?: string;
    departure: string;
    arrival: string;
    pnr?: string;
    originalDate?: string;
    originalClass?: string;
    newDate?: string;
    newClass?: string;
    cabinClass?: string;
    changeType?: "DATE_CHANGE" | "CLASS_UPGRADE" | "DATE_AND_CLASS";
    supplier?: string;
    supplierName?: string;
    mfUniqueId?: string;
    ptrId?: number | string;
    ptrStatus?: string;
    quoteSource?: QuoteSource;
    flowType?: FlowType;
    availabilityStatus?: string;
    supplierFareDiff?: number;
    changeFee?: number;
    taxDiff?: number;
    totalFareDiff?: number;
    paxRates?: Partial<Record<PaxType, PaxRates>>;
    issuanceStatus?: "PENDING" | "CONFIRMED" | "FAILED" | "PARTIAL" | "NOT_MODIFIED";
    newPnr?: string;
    oldPnr?: string;
    newTicketNumber?: string;
    route?: string;
    // per-pax e-tickets after issuance: one entry per passenger
    allTickets?: Array<{ paxType?: PaxType; firstName?: string; lastName?: string; ticketNumber: string }>;
    sectorUniqueKey?: string;
    quoteGroupKey?: string; // shared-PNR grouping key (round-trip = one group)
    // partial modify (split bookings): the supplier refused this sector
    unavailableReason?: string;
    // when an AUTO sector's auto-pricing failed and was flipped to MANUAL
    autoRescueReason?: string;
    // manual-issuance response: this sector was auto-completed from a shared-PNR sibling
    mirroredFrom?: string | null;
    issuanceCompletedAt?: string;
    // /status may nest passengers per sector (instead of a top-level passengers[]).
    passengers?: Array<{
        firstName?: string;
        lastName?: string;
        paxType?: PaxType;
        ticketNumber?: string;
        ticketStatus?: string;
    }>;
}

export interface QuoteSectorCharge {
    count: number;
    paxType: PaxType;
    lineTotal: number;
    taxPerPax: number;
    perPaxTotal: number;
    fareDiffPerPax: number;
    changeFeePerPax: number;
}

export interface QuoteSectorBreakdown {
    route: string;
    changeType: string;
    sectorTotal: number;
    transactionId: string;
    charges_by_type: QuoteSectorCharge[];
    sectorMarkup?: number; // per-sector markup ops entered (split bookings)
}

// Refused sector on a partial quote (split bookings only).
export interface UnavailableSector {
    transactionId: string;
    route: string;
    changeType?: string;
    modificationAvailable: boolean;
    unavailableReason?: string;
}

export interface QuoteDetails {
    markup: number;
    serviceFee?: number; // SMB-3206: customerTotal = supplierTotal + markup + serviceFee
    sectors: QuoteSectorBreakdown[];
    unavailableSectors?: UnavailableSector[]; // present only on partial quotes
    currency: string;
    markupSource?: string;
    customerTotal: number;
    supplierTotal: number;
}

export interface ModifyStatusResponse {
    modifyTransactionId: string;
    bookingId: string;
    status: BackendStatus;
    modifyType: "DATE_CHANGE" | "CLASS_UPGRADE" | "DATE_AND_CLASS" | "FULL_MODIFY";
    tripType: "1" | "2" | "3";
    version: number;
    sectors: Sector[];
    quoteDetails?: QuoteDetails;
    // paxSummary may be absent on /status; passengers[] (with paxType) is the
    // fallback for deriving per-pax-type counts.
    paxSummary?: Partial<Record<PaxType, number>>;
    passengers?: Array<{
        firstName?: string;
        lastName?: string;
        paxType?: PaxType;
        ticketNumber?: string;
        ticketStatus?: string;
        transactionId?: string;
    }>;
    customerTotal?: string;
    supplierTotal?: string;
    markup?: string;
    paymentStatus?: "PENDING" | "SUCCEEDED" | "FAILED" | "REFUNDED";
    paymentSessionId?: string;
    paymentFailureCount?: number;
    paymentFailureReason?: string;
    issuanceStatus?: "IN_PROGRESS" | "COMPLETE" | "FAILED" | "PARTIAL";
    issuanceStartedAt?: string;
    issuanceCompletedAt?: string;
    issuanceError?: string;
    supplierConfBefore?: string;
    supplierConfNew?: string;
    expiresAt?: string;
    quoteSentAt?: string;
    customerActionAt?: string;
    cancellationReason?: string;
    refundAmount?: number;
    refundMethod?: string;
    refundReference?: string;
    refundStripeId?: string;
    failureReason?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ApiEnvelope<T> {
    status: number;
    message?: string;
    data: T;
}

// Translate the backend status to the modState bucket used throughout the UI.
// Mapping per Modification-flow.docx v9 section 12.
export function statusToModState(status: BackendStatus): ModState {
    if (status === "IN_PROGRESS") return "new_request";
    if (status === "QUOTE_PENDING") return "checking";
    if (status === "QUOTE_READY") return "quote_ready";
    if (status === "CUST_REVIEW") return "sent_to_customer";
    if (
        status === "PAY_PENDING" ||
        status === "PAY_SUCCESS" ||
        status === "ISSUING" ||
        status === "ISSUED" ||
        status === "CONFIRMED" ||
        status === "COMPLETE"
    ) {
        return "customer_approved";
    }
    if (
        status === "CANCELLED" ||
        status === "QUOTE_EXPIRED" ||
        status === "AUTO_CANCELLED" ||
        status === "FAILED"
    ) {
        return "customer_rejected";
    }
    if (status === "ISSUANCE_FAILED" || status === "REFUND_PEND") return "refund_pending";
    if (status === "REFUND_COMPLETE") return "refund_complete";
    return "new_request";
}

export function isTerminalStatus(status: BackendStatus): boolean {
    return (
        status === "CONFIRMED" ||
        status === "COMPLETE" ||
        status === "CANCELLED" ||
        status === "AUTO_CANCELLED" ||
        status === "QUOTE_EXPIRED" ||
        status === "REFUND_COMPLETE" ||
        status === "FAILED"
    );
}
//********TYPES ENDS*********/

//********SERVICE FUNCTIONS STARTS*********/

// Workhorse: returns the full denormalized snapshot.
// Called first when ops opens the workflow drawer.
export const getModifyStatus = (
    modifyTransactionId: string,
    opts: object = {}
): Promise<ApiEnvelope<ModifyStatusResponse>> =>
    apiGet(`${MODIFY_BASE}/${modifyTransactionId}/status`, { ...opts, traceId: genTraceId() });

// Step 1: ask the supplier for availability + pricing.
export interface CheckAvailabilityBody {
    modifyTransactionId: string;
    itineraryId: string;
}
export interface CheckAvailabilityResponse {
    modifyTransactionId: string;
    status: BackendStatus;
    needsPolling?: boolean;
    sectors?: Sector[];
    quoteDetails?: QuoteDetails;
}
export const checkAvailability = (
    body: CheckAvailabilityBody,
    opts: object = {}
): Promise<ApiEnvelope<CheckAvailabilityResponse>> =>
    apiPost(`${MODIFY_BASE}/check-availability`, body, { ...opts, traceId: genTraceId() });

// Step 2 (ops): apply markup, persist manual sector quotes / refusals, email.
// MANUAL sectors go in a top-level `sectorQuotes` array with per-pax-type
// fareDiff / changeFee / tax (NO totalDiff -- backend derives it). AUTO sectors
// are priced by the supplier and ignored if sent. Markup placement (Section 4):
//   tripType "2" (shared PNR) -> top-level markupPct/markupAmt only
//   tripType "1" split, 2+ priced -> per-sector sectorQuotes[].markupAmt (or top-level markupPct)
//   per-sector markupAmt and top-level markup are mutually exclusive.
// Split refusal (Scenario 10): { transactionId, available:false, unavailableReason }.
export interface ManualQuoteSector {
    transactionId: string;
    paxRates: Partial<Record<PaxType, PaxRates>>;
}
// Submitted manual rate (no totalDiff).
export interface SendQuotePaxRate {
    fareDiff: number;
    changeFee: number;
    tax: number;
}
export interface SendQuoteSector {
    transactionId: string;
    paxRates?: Partial<Record<PaxType, SendQuotePaxRate>>;
    markupAmt?: number; // per-sector markup (split bookings only)
    available?: boolean; // false = supplier refused this sector (split only)
    unavailableReason?: string; // shown verbatim to the customer
}
export interface SendQuoteBody {
    modifyTransactionId: string;
    agentId?: string; // audit -- who priced/refused
    markupPct?: number;
    markupAmt?: number;
    sectorQuotes?: SendQuoteSector[];
}
export interface SendQuoteResponse {
    modifyTransactionId: string;
    status: BackendStatus;
    expiresAt: string;
    emailSent: boolean;
    emailRecipient: string;
    emailError: string | null;
    customerPrice: string;
}
export const sendQuote = (
    body: SendQuoteBody,
    opts: object = {}
): Promise<ApiEnvelope<SendQuoteResponse>> =>
    apiPost(`${MODIFY_BASE}/send-quote`, body, { ...opts, traceId: genTraceId() });

// Internal but ops can re-call during the 4h ISSUANCE_FAILED grace as a retry.
export interface TriggerIssuanceBody {
    modifyTransactionId: string;
    itineraryId: string;
}
export const triggerIssuance = (
    body: TriggerIssuanceBody,
    opts: object = {}
): Promise<ApiEnvelope<{ modifyTransactionId: string; status: BackendStatus }>> =>
    apiPost(`${MODIFY_BASE}/trigger-issuance`, body, { ...opts, traceId: genTraceId() });

// Ops finalizes a MANUAL (Mondee / Duffel) reissue after the supplier replies.
export interface ManualIssuanceTicket {
    firstName?: string;
    lastName?: string;
    paxType?: PaxType;
    ticketNumber: string;
}
// One reissue result per sector. tickets[] for multi-pax; newTicketNumber is a
// single-pax shortcut. Shared-PNR (tripType 2): enter ONE entry (backend mirrors
// to siblings). Split (tripType 1): one entry per priced sector.
export interface ManualIssuanceSectorResult {
    transactionId: string;
    newPnr: string;
    tickets?: ManualIssuanceTicket[];
    newTicketNumber?: string; // single-passenger sectors only
    newFareBasis?: string;
}
export interface ManualIssuanceBody {
    modifyTransactionId: string;
    agentId: string; // required -- who completed the reissue
    sectorResults: ManualIssuanceSectorResult[];
}
export interface ManualIssuanceResponse {
    modifyTransactionId: string;
    status: BackendStatus;
    summary?: { bookingTotal: number; bookingConfirmed: number; bookingPending: number; bookingFailed: number };
    sectors: Sector[];
}
export const manualIssuance = (
    body: ManualIssuanceBody,
    opts: object = {}
): Promise<ApiEnvelope<ManualIssuanceResponse>> =>
    apiPost(`${MODIFY_BASE}/manual-issuance`, body, { ...opts, traceId: genTraceId() });

// Trigger a retrieve-pnr poll mid-flow. Optional transactionIds[] narrows scope.
export interface FetchFromSupplierBody {
    modifyTransactionId: string;
    itineraryId: string;
    agentId?: string;
    transactionIds?: string[];
}
export interface FetchFromSupplierResponse {
    modifyTransactionId: string;
    status: BackendStatus;
    summary: {
        total: number;
        found: number;
        notReady: number;
        skipped: number;
    };
    message?: string;
    sectors: Sector[];
    notReadySectors: string[];
    skippedSectors: string[];
}
export const fetchFromSupplier = (
    body: FetchFromSupplierBody,
    opts: object = {}
): Promise<ApiEnvelope<FetchFromSupplierResponse>> =>
    apiPost(`${MODIFY_BASE}/fetch-from-supplier`, body, { ...opts, traceId: genTraceId() });

// Ops kills a stuck modify. If paid, triggers the refund cascade.
export interface CancelModifyBody {
    modifyTransactionId: string;
    itineraryId: string;
    cancelledBy: string;
    reason: "OPS_CANCELLED" | "CUSTOMER_REQUESTED" | "OTHER" | string;
}
export const cancelModify = (
    body: CancelModifyBody,
    opts: object = {}
): Promise<ApiEnvelope<{ modifyTransactionId: string; status: BackendStatus }>> =>
    apiPost(`${MODIFY_BASE}/cancel`, body, { ...opts, traceId: genTraceId() });

// Accounts marks the refund processed -- flips REFUND_PEND to REFUND_COMPLETE
// and sends the customer the refund-confirmation email.
export interface MarkRefundedBody {
    modifyTransactionId: string;
    itineraryId: string;
    refund_method?: string;
    refund_reference?: string;
    agentId?: string;
}
export interface MarkRefundedResponse {
    modifyTransactionId: string;
    status: BackendStatus;
    refundAmount: number;
    customerEmailSent: boolean;
}
export const markRefunded = (
    body: MarkRefundedBody,
    opts: object = {}
): Promise<ApiEnvelope<MarkRefundedResponse>> =>
    apiPost(`${MODIFY_BASE}/mark-refunded`, body, { ...opts, traceId: genTraceId() });

// Ops fast-path failure (SMB-2905). When ops has already confirmed by phone
// that the supplier rejected the reissue, skips the 24h ISSUING TTL and 4h
// grace and goes straight to REFUND_PEND.
export interface MarkIssuanceFailedBody {
    modifyTransactionId: string;
    itineraryId: string;
    failureReason: string;
    agentId?: string;
}
export interface MarkIssuanceFailedResponse {
    modifyTransactionId: string;
    status: BackendStatus;
    previousStatus: BackendStatus;
    failureReason: string;
    refundAmount: number;
    customerEmailDelivered: boolean;
    nextStep: string;
}
export const markIssuanceFailed = (
    body: MarkIssuanceFailedBody,
    opts: object = {}
): Promise<ApiEnvelope<MarkIssuanceFailedResponse>> =>
    apiPost(`${MODIFY_BASE}/mark-issuance-failed`, body, { ...opts, traceId: genTraceId() });
//********SERVICE FUNCTIONS ENDS*********/
